import { memo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  useFindingStore,
  Finding,
  FindingSeverityNameToIntMap,
} from "stores/FindingStore";

import dayjs from "utility/dayjs";
import { notifyError, notifySuccess } from "utility/notify";

import SeverityDropdown from "components/SeverityDropdown";

const FindingBulkSeverityDialog = memo(function OrganizationFindingBulkActions({
  findings,
  onClose,
}: FindingBulkSeverityDialogProps): JSX.Element {
  const [, findingActions] = useFindingStore();

  const [severity, setSeverity] = useState("Medium");
  const [loading, setLoading] = useState(false);

  const confirm = () => {
    setLoading(true);
    const uuids = findings.map((finding) => finding.uuid);

    findingActions.bulkUpdateSeverity({
      uuids,
      newSeverity: FindingSeverityNameToIntMap[severity],
      onSuccess,
      onError,
    });
  };

  const onSuccess = () => {
    setLoading(false);
    onClose();
    notifySuccess(
      `Successfully set the severity of the selected findings to ${severity}`
    );
  };

  const onError = () => {
    setLoading(false);
    onClose();
    notifyError(
      "Something went wrong trying to edit the severity of one or " +
        "more of the selected findings"
    );
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth>
      <DialogTitle>Edit Severity for Selected Findings</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            This will update the severity of the findings that you have
            selected.
          </DialogContentText>

          <SeverityDropdown severity={severity} onChange={setSeverity} />

          <Box>
            <Typography fontWeight="bold">
              Findings that will be updated:
            </Typography>
            <ul style={{ marginBottom: 0 }}>
              {(findings || []).map((finding) => {
                return (
                  <li key={finding.uuid}>
                    <Typography>{`${finding.name} from ${dayjs(
                      finding.create_time
                    ).format("MM/DD/YYYY")}`}</Typography>
                  </li>
                );
              })}
            </ul>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton loading={loading} onClick={confirm}>
          Update selected findings
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

export default FindingBulkSeverityDialog;

// -----------------------------------------------------------------------------

interface FindingBulkSeverityDialogProps {
  findings: Finding[];
  onClose: () => void;
}
